/**
 * Shopz.ae PDP - storefront logic
 * --------------------------------------------------------------
 * Vanilla JS, no dependencies. Built with custom elements so every
 * section re-initialises automatically inside the theme editor.
 *
 * Public API (window.Shopz):
 *   Shopz.formatMoney(cents, format)
 *   Shopz.addToCart(itemsArrayOrFormData)
 *   Shopz.refreshCartCount()
 *   Shopz.toast(message, { error, link: { href, label } })
 *
 * Events (listen on document):
 *   shopz:variant:change   detail: { variant, product }
 *   shopz:cart:added       detail: cart add response
 *   shopz:cart:updated     detail: full cart
 *   shopz:wishlist:change  detail: { handle, saved, list }
 */
(function () {
  'use strict';

  const Shopz = (window.Shopz = window.Shopz || {});
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------
   * Money formatting (supports every Shopify money format token)
   * ------------------------------------------------------------------ */
  Shopz.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = String(format || Shopz.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const rx = /\{\{\s*(\w+)\s*\}\}/;
    const delimit = (n, precision, thousands = ',', decimal = '.') => {
      if (n == null || isNaN(n)) return '0';
      const parts = (n / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    };
    const m = fmt.match(rx);
    if (!m) return fmt;
    let value;
    switch (m[1]) {
      case 'amount_no_decimals': value = delimit(cents, 0); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = delimit(cents, 0, ' ', '.'); break;
      case 'amount_with_space_separator': value = delimit(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = delimit(cents, 2, ' ', '.'); break;
      default: value = delimit(cents, 2);
    }
    return fmt.replace(rx, value);
  };

  /* ------------------------------------------------------------------
   * Toast notification
   * ------------------------------------------------------------------ */
  let toastTimer;
  Shopz.toast = function (message, opts = {}) {
    let el = document.querySelector('.sz-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'sz-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.classList.toggle('is-error', !!opts.error);
    el.innerHTML = '';
    const icon = document.createElement('span');
    icon.className = 'sz-toast__icon';
    icon.textContent = opts.error ? '!' : '\u2713';
    const text = document.createElement('span');
    text.textContent = message;
    el.append(icon, text);
    if (opts.link) {
      const a = document.createElement('a');
      a.className = 'sz-toast__link';
      a.href = opts.link.href;
      a.textContent = opts.link.label;
      el.appendChild(a);
    }
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), opts.duration || 3500);
  };

  /* ------------------------------------------------------------------
   * Cart helpers
   * ------------------------------------------------------------------ */
  // Theme sections re-rendered after add (Dawn-style cart bubble). Edit if your theme differs.
  Shopz.cartSections = ['cart-icon-bubble'];

  Shopz.addToCart = async function (payload) {
    let options;
    if (payload instanceof FormData) {
      payload.append('sections', Shopz.cartSections.join(','));
      payload.append('sections_url', window.location.pathname);
      options = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: payload };
    } else {
      options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: payload, sections: Shopz.cartSections.join(','), sections_url: window.location.pathname })
      };
    }
    const res = await fetch(root() + 'cart/add.js', options);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    Shopz.renderCartSections(data.sections);
    Shopz.refreshCartCount();
    document.dispatchEvent(new CustomEvent('shopz:cart:added', { detail: data }));
    return data;
  };

  // Replace the theme's cart bubble with fresh HTML returned by the Section Rendering API
  Shopz.renderCartSections = function (sections) {
    if (!sections) return;
    const html = sections['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    if (html && target) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const src = doc.querySelector('.shopify-section');
      if (src) target.innerHTML = src.innerHTML;
    }
  };

  // Update any element with [data-cart-count] / [data-cart-total] in the header
  Shopz.refreshCartCount = async function () {
    try {
      const res = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
      document.querySelectorAll('[data-cart-total]').forEach((el) => { el.textContent = Shopz.formatMoney(cart.total_price); });
      document.dispatchEvent(new CustomEvent('shopz:cart:updated', { detail: cart }));
      return cart;
    } catch (e) {
      return null;
    }
  };

  // Button loading state (keeps sold-out buttons disabled)
  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.disabled = on ? true : btn.dataset.forceDisabled === 'true';
  }

  /* ------------------------------------------------------------------
   * Wishlist (localStorage; swap with an app via the shopz:wishlist:change event)
   * ------------------------------------------------------------------ */
  // Same list as the rest of the store (header ♥ count, wishlist page, product cards)
  const WL_KEY = 'toy-wishlist';
  const wlGet = () => { try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch (e) { return []; } };
  const wlSet = (list) => { try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch (e) { /* storage blocked */ } };

  Shopz.syncWishlist = function () {
    const list = wlGet();
    document.querySelectorAll('[data-sz-wishlist]').forEach((btn) => {
      const on = list.includes(btn.dataset.szWishlist);
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  /* ------------------------------------------------------------------
   * Global delegated clicks: wishlist, quick add, smooth scroll
   * ------------------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    // Wishlist toggle
    const wish = e.target.closest('[data-sz-wishlist]');
    if (wish) {
      e.preventDefault();
      const handle = wish.dataset.szWishlist;
      let list = wlGet();
      const saved = !list.includes(handle);
      list = saved ? list.concat(handle) : list.filter((h) => h !== handle);
      wlSet(list);
      Shopz.syncWishlist();
      Shopz.toast(saved ? 'Saved to wishlist' : 'Removed from wishlist');
      document.dispatchEvent(new CustomEvent('shopz:wishlist:change', { detail: { handle, saved, list } }));
      return;
    }

    // Quick add from product cards
    const quick = e.target.closest('[data-sz-quick-add]');
    if (quick) {
      e.preventDefault();
      setLoading(quick, true);
      try {
        await Shopz.addToCart([{ id: Number(quick.dataset.szQuickAdd), quantity: 1 }]);
        Shopz.toast(quick.dataset.successText || 'Added to cart', { link: { href: root() + 'cart', label: 'View cart' } });
      } catch (err) {
        Shopz.toast(err.message, { error: true });
      } finally {
        setLoading(quick, false);
      }
      return;
    }

    // Smooth scroll links (e.g. "124 reviews" -> reviews section)
    const scroller = e.target.closest('[data-sz-scroll]');
    if (scroller) {
      const target = document.querySelector(scroller.getAttribute('href'));
      if (target) {
        e.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  });

  /* ==================================================================
   * <sz-product-info> : gallery + variant picker + add to cart
   * ================================================================== */
  class SzProductInfo extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const dataEl = this.querySelector('[data-sz-product]');
      try { this.product = JSON.parse(dataEl.textContent); } catch (e) { this.product = { variants: [] }; }
      this.variants = this.product.variants || [];
      this.moneyFormat = this.product.moneyFormat;
      Shopz.moneyFormat = Shopz.moneyFormat || this.moneyFormat;

      this.slides = Array.from(this.querySelectorAll('[data-sz-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-sz-thumb]'));
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.goToken = 0;

      this.form = this.querySelector('[data-sz-form]');
      this.idInput = this.querySelector('[data-sz-variant-id]');
      const currentId = this.idInput ? Number(this.idInput.value) : null;
      this.variant = this.variants.find((v) => v.id === currentId) || this.variants[0] || null;

      this.initGallery();
      this.initOptions();
      this.initQty();
      this.initForm();
      this.initActions();
      this.initDelivery();
      this.refreshAvailability();
      Shopz.syncWishlist();
    }

    disconnectedCallback() {
      clearInterval(this.deliveryTimer);
    }

    /* ---------------- Gallery ---------------- */
    initGallery() {
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));

      const prev = this.querySelector('[data-sz-prev]');
      const next = this.querySelector('[data-sz-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

      // Keyboard navigation on the thumbnail list
      const thumbsWrap = this.querySelector('[data-sz-thumbs]');
      if (thumbsWrap) {
        thumbsWrap.addEventListener('keydown', (e) => {
          if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
          e.preventDefault();
          const dir = e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 1;
          const target = (this.index + dir + this.slides.length) % this.slides.length;
          this.goTo(target);
          if (this.thumbs[target]) this.thumbs[target].focus();
        });
      }

      // Swipe on touch devices
      const stage = this.querySelector('[data-sz-stage]');
      if (stage && this.slides.length > 1) {
        let sx = 0, sy = 0, tracking = false;
        stage.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
          tracking = true;
        }, { passive: true });
        stage.addEventListener('touchend', (e) => {
          if (!tracking) return;
          tracking = false;
          const t = e.changedTouches[0];
          const dx = t.clientX - sx;
          const dy = t.clientY - sy;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.goTo(this.index + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }

      // Warm up the next image so the first transition is instant
      const warm = () => this.preload(this.index + 1);
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm); else setTimeout(warm, 1200);
    }

    preload(i) {
      const n = this.slides.length;
      if (!n) return;
      const slide = this.slides[((i % n) + n) % n];
      const img = slide && slide.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }

    // Fade to slide i. The image is decoded first, so the swap never shows a blank frame.
    async goTo(i) {
      const n = this.slides.length;
      if (n < 2) return;
      i = ((i % n) + n) % n;
      if (i === this.index) return;

      const token = ++this.goToken;
      this.setActiveThumb(i);

      const target = this.slides[i];
      const img = target.querySelector('img');
      if (img) {
        if (img.loading === 'lazy') img.loading = 'eager';
        if (!img.complete || !img.naturalWidth) {
          try { await img.decode(); } catch (e) { /* show anyway */ }
        }
      }
      if (token !== this.goToken) return; // a newer navigation happened meanwhile

      const current = this.slides[this.index];
      this.pauseMedia(current);
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      target.classList.add('is-active');
      target.removeAttribute('aria-hidden');
      this.index = i;
      this.preload(i + 1);
    }

    setActiveThumb(i) {
      this.thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === i);
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
      const counter = this.querySelector('[data-sz-current]');
      if (counter) counter.textContent = i + 1;

      // Keep the active thumbnail visible inside its own scroller (never scrolls the page)
      const thumb = this.thumbs[i];
      const wrap = thumb && thumb.parentElement;
      if (!wrap) return;
      const wr = wrap.getBoundingClientRect();
      const tr = thumb.getBoundingClientRect();
      if (tr.top < wr.top) wrap.scrollBy({ top: tr.top - wr.top - 6, behavior: 'smooth' });
      else if (tr.bottom > wr.bottom) wrap.scrollBy({ top: tr.bottom - wr.bottom + 6, behavior: 'smooth' });
      if (tr.left < wr.left) wrap.scrollBy({ left: tr.left - wr.left - 6, behavior: 'smooth' });
      else if (tr.right > wr.right) wrap.scrollBy({ left: tr.right - wr.right + 6, behavior: 'smooth' });
    }

    pauseMedia(slide) {
      if (!slide) return;
      slide.querySelectorAll('video').forEach((v) => v.pause());
      slide.querySelectorAll('iframe').forEach((f) => {
        try {
          f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); // YouTube
          f.contentWindow.postMessage('{"method":"pause"}', '*'); // Vimeo
        } catch (e) { /* cross-origin safe */ }
      });
    }

    /* ---------------- Variant options ---------------- */
    initOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('[data-sz-option]'));
      this.fieldsets.forEach((fs) => fs.addEventListener('change', () => this.onOptionChange()));
    }

    selectedOptions() {
      return this.fieldsets.map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.fieldsets.forEach((fs, k) => {
        const out = fs.querySelector('[data-sz-option-value]');
        if (out) out.textContent = selected[k] || '';
      });
      const variant = this.variants.find((v) => v.options.every((o, k) => o === selected[k]));
      this.refreshAvailability();
      this.updateVariant(variant);
    }

    // Mark option values that cannot be bought with the other current selections
    refreshAvailability() {
      if (!this.fieldsets || !this.fieldsets.length) return;
      const selected = this.selectedOptions();
      this.fieldsets.forEach((fs, k) => {
        fs.querySelectorAll('input').forEach((input) => {
          const test = selected.slice();
          test[k] = input.value;
          const ok = this.variants.some((v) => v.available && v.options.every((o, j) => o === test[j]));
          const label = input.closest('[data-sz-value]');
          if (label) label.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    updateVariant(v) {
      this.variant = v || null;
      const addBtn = this.querySelector('[data-sz-add]');

      if (!v) {
        this.setButtons(false, addBtn && addBtn.dataset.unavailableText);
        return;
      }

      if (this.idInput) this.idInput.value = v.id;
      const fm = (c) => Shopz.formatMoney(c, this.moneyFormat);
      const onSale = !!(v.compare_at_price && v.compare_at_price > v.price);

      this.setText('[data-sz-price]', fm(v.price));
      this.setText('[data-sz-compare]', onSale ? fm(v.compare_at_price) : '');
      this.toggle('[data-sz-compare]', onSale);
      this.setText('[data-sz-save-amount]', onSale ? fm(v.compare_at_price - v.price) : '');
      this.toggle('[data-sz-save]', onSale);

      const pct = onSale ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0;
      this.setText('[data-sz-discount-value]', pct);
      this.toggle('[data-sz-discount]', pct > 0);

      const tabby = this.querySelector('[data-sz-tabby]');
      if (tabby) tabby.textContent = fm(Math.ceil(v.price / Number(tabby.dataset.count || 4)));

      const stock = this.querySelector('[data-sz-stock]');
      if (stock) {
        stock.classList.toggle('is-out', !v.available);
        const t = stock.querySelector('[data-sz-stock-text]');
        if (t) t.textContent = v.available ? stock.dataset.inText : stock.dataset.outText;
      }

      this.setButtons(v.available, addBtn && (v.available ? addBtn.dataset.addText : addBtn.dataset.soldOutText));

      // Keep ?variant= in the URL for sharing / refresh
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());

      // Switch to the variant image with the fade animation
      if (v.featured_media) {
        const idx = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(v.featured_media.id));
        if (idx > -1) this.goTo(idx);
      }

      this.dispatchEvent(new CustomEvent('shopz:variant:change', { bubbles: true, detail: { variant: v, product: this.product } }));
    }

    setButtons(available, label) {
      const addBtn = this.querySelector('[data-sz-add]');
      const buyBtn = this.querySelector('[data-sz-buy-now]');
      [addBtn, buyBtn].forEach((btn) => {
        if (!btn) return;
        btn.dataset.forceDisabled = available ? 'false' : 'true';
        btn.disabled = !available;
      });
      const text = this.querySelector('[data-sz-add-text]');
      if (text && label) text.textContent = label;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((el) => { el.textContent = value; });
    }

    toggle(selector, show) {
      this.querySelectorAll(selector).forEach((el) => { el.hidden = !show; });
    }

    /* ---------------- Quantity ---------------- */
    initQty() {
      this.qtyInput = this.querySelector('[data-sz-qty-input]');
      this.querySelectorAll('[data-sz-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.szQty === 'plus' ? 1 : -1;
          this.qtyInput.value = Math.max(1, this.qty() + step);
        });
      });
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => { this.qtyInput.value = this.qty(); });
      }
    }

    qty() {
      const n = parseInt(this.qtyInput && this.qtyInput.value, 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    /* ---------------- Add to cart / Buy now ---------------- */
    buildFormData() {
      // FormData keeps app inputs (properties[...], selling_plan) automatically
      const fd = new FormData(this.form);
      fd.set('id', this.variant.id);
      fd.set('quantity', this.qty());
      return fd;
    }

    initForm() {
      if (!this.form) return;
      this.errorEl = this.querySelector('[data-sz-error]');

      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!this.variant || !this.variant.available) return;
        const btn = this.querySelector('[data-sz-add]');
        this.showError('');
        setLoading(btn, true);
        try {
          await Shopz.addToCart(this.buildFormData());
          Shopz.toast(btn.dataset.successText || 'Added to cart', {
            link: { href: root() + 'cart', label: btn.dataset.viewCartText || 'View cart' }
          });
        } catch (err) {
          this.showError(err.message);
          Shopz.toast(err.message, { error: true });
        } finally {
          setLoading(btn, false);
        }
      });

      // Buy now: add the item, then go straight to checkout
      const buy = this.querySelector('[data-sz-buy-now]');
      if (buy) {
        buy.addEventListener('click', async () => {
          if (!this.variant || !this.variant.available) return;
          this.showError('');
          setLoading(buy, true);
          try {
            await Shopz.addToCart(this.buildFormData());
            window.location.href = root() + 'checkout';
          } catch (err) {
            this.showError(err.message);
            setLoading(buy, false);
          }
        });
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    /* ---------------- Share + zoom ---------------- */
    initActions() {
      const share = this.querySelector('[data-sz-share]');
      if (share) {
        share.addEventListener('click', async () => {
          const data = { title: document.title, url: window.location.href };
          if (navigator.share) {
            try { await navigator.share(data); } catch (e) { /* user cancelled */ }
            return;
          }
          try {
            await navigator.clipboard.writeText(window.location.href);
            Shopz.toast(share.dataset.copiedText || 'Link copied');
          } catch (e) {
            Shopz.toast(window.location.href);
          }
        });
      }
      const zoom = this.querySelector('[data-sz-zoom]');
      if (zoom) zoom.addEventListener('click', () => this.openLightbox());
    }

    openLightbox() {
      const slide = this.slides[this.index];
      const src = slide && slide.dataset.zoom;
      if (!src) return;
      const original = slide.querySelector('img');

      const lb = document.createElement('div');
      lb.className = 'sz-lightbox';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.innerHTML = '<button type="button" class="sz-lightbox__close" aria-label="Close"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button><img class="sz-lightbox__img" alt="">';
      const img = lb.querySelector('img');
      img.src = src;
      img.alt = original ? original.alt : '';

      document.body.appendChild(lb);
      document.documentElement.classList.add('sz-lock');
      requestAnimationFrame(() => lb.classList.add('is-open'));

      const zoomBtn = this.querySelector('[data-sz-zoom]');
      const onKey = (e) => { if (e.key === 'Escape') close(); };
      const close = () => {
        lb.classList.remove('is-open');
        document.documentElement.classList.remove('sz-lock');
        document.removeEventListener('keydown', onKey);
        setTimeout(() => lb.remove(), 260);
        if (zoomBtn) zoomBtn.focus();
      };
      lb.addEventListener('click', (e) => {
        if (e.target === lb || e.target.closest('.sz-lightbox__close')) close();
      });
      document.addEventListener('keydown', onKey);
      lb.querySelector('.sz-lightbox__close').focus();
    }

    /* ---------------- Delivery estimate + countdown ---------------- */
    initDelivery() {
      const el = this.querySelector('[data-sz-delivery]');
      if (!el) return;
      const dateEl = el.querySelector('[data-sz-delivery-date]');
      const countEl = el.querySelector('[data-sz-delivery-countdown]');

      const render = () => {
        const cutoff = Number(el.dataset.cutoff || 18);
        const days = Number(el.dataset.days || 1);
        const opts = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' };
        let parts;
        try {
          parts = new Intl.DateTimeFormat('en-GB', Object.assign({ timeZone: el.dataset.tz || undefined }, opts)).formatToParts(new Date());
        } catch (e) {
          parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(new Date());
        }
        const get = (type) => Number((parts.find((p) => p.type === type) || {}).value || 0);
        const hour = get('hour') % 24;
        let minutesLeft = cutoff * 60 - (hour * 60 + get('minute'));
        let extraDay = 0;
        if (minutesLeft <= 0) { minutesLeft += 1440; extraDay = 1; }

        const totalDays = days + extraDay;
        const base = Date.UTC(get('year'), get('month') - 1, get('day'));
        const target = new Date(base + totalDays * 86400000);
        const dateStr = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(target);
        let prefix;
        if (totalDays === 0) prefix = el.dataset.today || 'Today';
        else if (totalDays === 1) prefix = el.dataset.tomorrow || 'Tomorrow';
        else prefix = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long' }).format(target);
        if (dateEl) dateEl.textContent = prefix + ', ' + dateStr;

        if (countEl) {
          const hrs = Math.floor(minutesLeft / 60);
          const mins = minutesLeft % 60;
          const time = (hrs ? hrs + (hrs === 1 ? ' hr ' : ' hrs ') : '') + mins + (mins === 1 ? ' min' : ' mins');
          countEl.textContent = (el.dataset.countdown || 'Order within {time}').replace('{time}', time);
        }
      };

      render();
      this.deliveryTimer = setInterval(render, 60000);
    }
  }

  /* ==================================================================
   * <sz-tabs> : accessible tabs
   * ================================================================== */
  class SzTabs extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.tabs = Array.from(this.querySelectorAll('[data-sz-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-sz-panel]'));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab.dataset.szTab)));
      this.addEventListener('keydown', (e) => {
        if (!e.target.matches('[data-sz-tab]') || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const i = this.tabs.indexOf(e.target);
        const next = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
        this.select(next.dataset.szTab);
        next.focus();
      });
    }

    select(name) {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.szTab === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.szPanel !== name; });
      // Clear active state on non-tab links (e.g. Reviews) too
      this.querySelectorAll('a.sz-tabs__btn').forEach((a) => a.classList.remove('is-active'));
    }
  }

  /* ==================================================================
   * <sz-related> : recommendations loader + carousel arrows
   * ================================================================== */
  class SzRelated extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-sz-track]');
      this.prev = this.querySelector('[data-sz-related-prev]');
      this.next = this.querySelector('[data-sz-related-next]');
      this.bindArrows();
      if (this.dataset.url) this.load(this.dataset.url);
      else this.updateArrows();
    }

    async load(url) {
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-sz-track]');
        if (fresh && fresh.querySelector('.sz-card:not(.sz-card--skeleton)')) {
          this.track.innerHTML = fresh.innerHTML;
          Shopz.syncWishlist();
          this.updateArrows();
        } else {
          this.hideSection();
        }
      } catch (e) {
        this.hideSection();
      }
    }

    hideSection() {
      const section = this.closest('.sz-section');
      if (section) section.style.display = 'none';
    }

    step() {
      const card = this.track.querySelector('.sz-card');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 20;
      return card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    }

    bindArrows() {
      if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -this.step(), behavior: 'smooth' }));
      if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: this.step(), behavior: 'smooth' }));
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.updateArrows());
      }, { passive: true });
      window.addEventListener('resize', () => this.updateArrows());
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }

  if (!customElements.get('sz-product-info')) customElements.define('sz-product-info', SzProductInfo);
  if (!customElements.get('sz-tabs')) customElements.define('sz-tabs', SzTabs);
  if (!customElements.get('sz-related')) customElements.define('sz-related', SzRelated);

  // Keep wishlist hearts in sync when the page is restored from back/forward cache
  window.addEventListener('pageshow', () => Shopz.syncWishlist());
})();